import { useMemo } from 'react';
import { MainFilterBar } from 'src/components/MainFilterBar';
import { Content, useContent } from 'src/contexts/ContentContext';
import { DialogProvider } from 'src/contexts/DialogContext/DialogContext';
import {
  EventDiscoveryContextProvider,
  useEventDiscoveryContext,
} from 'src/contexts/EventDiscoveryContext/EventDiscoveryContext';
import { FilterQueryContextProvider } from 'src/contexts/FilterQueryContext';
import {
  MultiSelectionContextProvider,
  MultiSelectScope,
  NO_GROUP_ID,
} from 'src/contexts/MultiSelectionContext';
import { SidePanelProvider } from 'src/contexts/SidePanelContext';
import * as Tabs from 'src/core/ui/Tabs';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { LayoutContent } from 'src/navigations/LayoutContent';
import { ContentId } from 'src/utils/constants/contentId';
import {
  DefaultOnSaleEventQuery,
  EmptyOnSaleEventQuery,
} from 'src/utils/eventQueryUtils';
import { Feature, OnSaleEventQuery, UserSetting } from 'src/WebApiController';

import { MainRoute } from '../MainRoute';
import { Notifications } from './Notifications/Notifications';
import * as styles from './OnSaleDiscovery.css';
import { OnSaleDiscoveryView } from './OnSaleDiscoveryView';

export const DISCOVERY_USER_SETTINGS = [
  UserSetting.QuickFiltersStateOnSaleEvent,
  UserSetting.OnSaleEventColumnEditability,
  UserSetting.OnSaleEventColumnNumberPrecision,
  UserSetting.OnSaleEventColumnsEnabled,
  UserSetting.OnSaleEventColumnsOrder,
  UserSetting.OnSaleEventColumnsWidths,
  UserSetting.OnSaleEventCustomColumns,
];

export function OnSaleDiscovery() {
  const title = useContent(ContentId.EventDiscovery);
  const hasEventDiscoveryNotificationsFeature = useUserHasFeature(
    Feature.EventDiscoveryNotifications
  );

  return (
    <FilterQueryContextProvider<OnSaleEventQuery>
      initialQuery={DefaultOnSaleEventQuery}
      emptyQuery={EmptyOnSaleEventQuery}
      saveQueryInUrl
    >
      <DialogProvider>
        <LayoutContent mainRoute={MainRoute.Discovery} routeTitle={title}>
          {hasEventDiscoveryNotificationsFeature ? (
            <Tabs.Root
              defaultValue="explore"
              style={{ width: '100%', height: '100%', gap: 0 }}
            >
              <Tabs.List
                showScrollEdges={false}
                rootClassName={styles.tabsList}
              >
                <Tabs.Trigger value="explore">
                  <Content id={ContentId.Explore}></Content>
                </Tabs.Trigger>
                <Tabs.Trigger value="alerts">
                  <Content id={ContentId.Notifications}></Content>
                </Tabs.Trigger>
              </Tabs.List>

              <Tabs.Content value="explore" style={{ height: '100%' }}>
                <EventDiscoveryContextProvider>
                  <OnSaleDiscoveryContent />
                </EventDiscoveryContextProvider>
              </Tabs.Content>

              <Tabs.Content value="alerts" style={{ height: '100%' }}>
                <Notifications />
              </Tabs.Content>
            </Tabs.Root>
          ) : (
            <EventDiscoveryContextProvider>
              <OnSaleDiscoveryContent />
            </EventDiscoveryContextProvider>
          )}
        </LayoutContent>
      </DialogProvider>
    </FilterQueryContextProvider>
  );
}

export const OnSaleDiscoveryContent = () => {
  const { onSaleEvents } = useEventDiscoveryContext();
  const allOnSaleEventIds = useMemo(
    () =>
      onSaleEvents
        .map((event) => event.event.viagId)
        .filter((id: number | null): id is number => id != null) ?? [],
    [onSaleEvents]
  );

  return (
    <MultiSelectionContextProvider
      isFlattenedView
      initialSelectionMode={{
        mode: MultiSelectScope.SingleGroup,
        groupId: NO_GROUP_ID,
      }}
      overrideflattenedIds={allOnSaleEventIds}
      overrideGroupsArray={[
        {
          id: NO_GROUP_ID,
          itemCount: allOnSaleEventIds.length,
          unfilteredItemCount: 0,
        },
      ]}
    >
      <SidePanelProvider sidePanelId="event-discovery" panelType={'content'}>
        <MainFilterBar />
        <OnSaleDiscoveryView />
      </SidePanelProvider>
    </MultiSelectionContextProvider>
  );
};
